import React, { useState, useEffect } from 'react';
import {
  TextField,
  TextFieldProps,
  InputAdornment,
  IconButton,
  Popover,
  Box,
  Typography,
  Button as MuiButton,
} from '@mui/material';
import {
  CalendarToday,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { toThaiYear, toCEYear, THAI_MONTHS_SHORT } from '../../utils/thaiDate';

export interface ThaiDatePickerProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  disableFuture?: boolean;
}

/**
 * Thai Date Picker Component
 * Date picker with Buddhist Era (พ.ศ.) calendar support
 */
const ThaiDatePicker: React.FC<ThaiDatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  disablePast = false,
  disableFuture = false,
  ...textFieldProps
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [displayMonth, setDisplayMonth] = useState<Date>(value || new Date());
  const [inputValue, setInputValue] = useState<string>('');
  const [viewMode, setViewMode] = useState<'day' | 'month' | 'year'>('day');

  useEffect(() => {
    if (value) {
      const day = String(value.getDate()).padStart(2, '0');
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const year = toThaiYear(value);
      setInputValue(`${day}/${month}/${year}`);
      setDisplayMonth(value);
    } else {
      setInputValue('');
    }
  }, [value]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setViewMode('day'); // Reset to day view when opening
  };

  const handleClose = () => {
    setAnchorEl(null);
    setViewMode('day'); // Reset to day view when closing
  };

  const handleDateSelect = (date: Date) => {
    if (onChange) {
      onChange(date);
    }
    handleClose();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);

    // Try to parse the date (DD/MM/YYYY in Thai year)
    const parts = newValue.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const thaiYear = parseInt(parts[2], 10);
      const ceYear = toCEYear(thaiYear);

      if (!isNaN(day) && !isNaN(month) && !isNaN(ceYear)) {
        const date = new Date(ceYear, month, day);
        if (date.getDate() === day && date.getMonth() === month) {
          if (onChange) {
            onChange(date);
          }
        }
      }
    }
  };

  const handlePrevMonth = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1));
  };

  const handleToday = () => {
    const today = new Date();
    handleDateSelect(today);
  };

  const handleClear = () => {
    if (onChange) {
      onChange(null);
    }
    setInputValue('');
  };

  const isDateDisabled = (date: Date): boolean => {
    // Normalize all dates to midnight for proper comparison
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (disablePast && normalizedDate < today) return true;
    if (disableFuture && normalizedDate > today) return true;
    
    if (minDate) {
      const normalizedMinDate = new Date(minDate);
      normalizedMinDate.setHours(0, 0, 0, 0);
      if (normalizedDate < normalizedMinDate) return true;
    }
    
    if (maxDate) {
      const normalizedMaxDate = new Date(maxDate);
      normalizedMaxDate.setHours(0, 0, 0, 0);
      if (normalizedDate > normalizedMaxDate) return true;
    }

    return false;
  };

  const handleYearSelect = (year: number) => {
    setDisplayMonth(new Date(year, displayMonth.getMonth(), 1));
    setViewMode('month');
  };

  const handleMonthSelect = (month: number) => {
    setDisplayMonth(new Date(displayMonth.getFullYear(), month, 1));
    setViewMode('day');
  };

  const handleHeaderClick = () => {
    if (viewMode === 'day') {
      setViewMode('month');
    } else if (viewMode === 'month') {
      setViewMode('year');
    }
  };

  const handlePrevYear = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear() - 1, displayMonth.getMonth(), 1));
  };

  const handleNextYear = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear() + 1, displayMonth.getMonth(), 1));
  };

  const handlePrevDecade = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear() - 10, displayMonth.getMonth(), 1));
  };

  const handleNextDecade = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear() + 10, displayMonth.getMonth(), 1));
  };

  const renderYearView = () => {
    const currentYear = displayMonth.getFullYear();
    const startYear = Math.floor(currentYear / 10) * 10;
    const years: number[] = [];

    for (let i = startYear - 1; i < startYear + 11; i++) {
      years.push(i);
    }

    return (
      <Box sx={{ p: 2, width: 320 }}>
        {/* Year Range Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <IconButton size="small" onClick={handlePrevDecade}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="subtitle1" fontWeight="bold">
            {toThaiYear(new Date(startYear, 0, 1))} - {toThaiYear(new Date(startYear + 9, 0, 1))}
          </Typography>
          <IconButton size="small" onClick={handleNextDecade}>
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Year Grid */}
        <Box display="flex" flexWrap="wrap" gap={1}>
          {years.map((year) => {
            const isCurrentYear = year === new Date().getFullYear();
            const isSelectedYear = value && year === value.getFullYear();
            const isOutOfRange = year < startYear || year >= startYear + 10;

            return (
              <Box
                key={year}
                onClick={() => !isOutOfRange && handleYearSelect(year)}
                sx={{
                  width: 'calc((100% - 3 * 8px) / 4)',
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  cursor: isOutOfRange ? 'default' : 'pointer',
                  backgroundColor: isSelectedYear ? 'primary.main' : 'transparent',
                  color: isSelectedYear ? 'white' : isOutOfRange ? 'text.disabled' : 'text.primary',
                  border: isCurrentYear && !isSelectedYear ? '1px solid' : 'none',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: isOutOfRange
                      ? 'transparent'
                      : isSelectedYear
                      ? 'primary.dark'
                      : 'action.hover',
                  },
                  fontSize: '0.875rem',
                  fontWeight: isCurrentYear ? 'bold' : 'normal',
                }}
              >
                {toThaiYear(new Date(year, 0, 1))}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderMonthView = () => {
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ];

    return (
      <Box sx={{ p: 2, width: 320 }}>
        {/* Year Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <IconButton size="small" onClick={handlePrevYear}>
            <ChevronLeft />
          </IconButton>
          <Typography 
            variant="subtitle1" 
            fontWeight="bold"
            onClick={handleHeaderClick}
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            {toThaiYear(displayMonth)}
          </Typography>
          <IconButton size="small" onClick={handleNextYear}>
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Month Grid */}
        <Box display="flex" flexWrap="wrap" gap={1}>
          {months.map((month, index) => {
            const isCurrentMonth = index === new Date().getMonth() && 
                                   displayMonth.getFullYear() === new Date().getFullYear();
            const isSelectedMonth = value && 
                                   index === value.getMonth() && 
                                   displayMonth.getFullYear() === value.getFullYear();

            return (
              <Box
                key={index}
                onClick={() => handleMonthSelect(index)}
                sx={{
                  width: 'calc((100% - 2 * 8px) / 3)',
                  height: 56,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: isSelectedMonth ? 'primary.main' : 'transparent',
                  color: isSelectedMonth ? 'white' : 'text.primary',
                  border: isCurrentMonth && !isSelectedMonth ? '1px solid' : 'none',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: isSelectedMonth ? 'primary.dark' : 'action.hover',
                  },
                  fontSize: '0.875rem',
                  fontWeight: isCurrentMonth ? 'bold' : 'normal',
                }}
              >
                {month}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderCalendar = () => {
    const year = displayMonth.getFullYear();
    const month = displayMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <Box sx={{ p: 2, width: 320 }}>
        {/* Month/Year Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <IconButton size="small" onClick={handlePrevMonth}>
            <ChevronLeft />
          </IconButton>
          <Typography 
            variant="subtitle1" 
            fontWeight="bold"
            onClick={handleHeaderClick}
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            {THAI_MONTHS_SHORT[month + 1]} {toThaiYear(displayMonth)}
          </Typography>
          <IconButton size="small" onClick={handleNextMonth}>
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Day of Week Headers */}
        <Box display="flex" gap={0.5} mb={1}>
          {['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'].map((day) => (
            <Box
              key={day}
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ height: 32, fontWeight: 'bold', fontSize: '0.875rem' }}
            >
              {day}
            </Box>
          ))}
        </Box>

        {/* Calendar Days */}
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {days.map((date, index) => {
            if (!date) {
              return (
                <Box
                  key={`empty-${index}`}
                  sx={{ width: 'calc((100% - 6 * 4px) / 7)', height: 32 }}
                />
              );
            }

            const isSelected = value && date.toDateString() === value.toDateString();
            const isToday = date.toDateString() === today.toDateString();
            const disabled = isDateDisabled(date);

            return (
              <Box
                key={index}
                onClick={() => !disabled && handleDateSelect(date)}
                sx={{
                  width: 'calc((100% - 6 * 4px) / 7)',
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  backgroundColor: isSelected ? 'primary.main' : 'transparent',
                  color: isSelected ? 'white' : disabled ? 'text.disabled' : 'text.primary',
                  border: isToday && !isSelected ? '1px solid' : 'none',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: disabled
                      ? 'transparent'
                      : isSelected
                      ? 'primary.dark'
                      : 'action.hover',
                  },
                  fontSize: '0.875rem',
                }}
              >
                {date.getDate()}
              </Box>
            );
          })}
        </Box>

        {/* Action Buttons */}
        <Box display="flex" gap={1} mt={2}>
          <MuiButton size="small" variant="outlined" onClick={handleToday} fullWidth>
            วันนี้
          </MuiButton>
          <MuiButton size="small" variant="outlined" onClick={handleClear} fullWidth>
            ล้าง
          </MuiButton>
        </Box>
      </Box>
    );
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <TextField
        {...textFieldProps}
        value={inputValue}
        onChange={handleInputChange}
        placeholder="DD/MM/YYYY (พ.ศ.)"
        InputProps={{
          ...textFieldProps.InputProps,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton 
                onClick={handleOpen} 
                edge="end"
                disabled={textFieldProps.disabled}
              >
                <CalendarToday />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {viewMode === 'year' && renderYearView()}
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'day' && renderCalendar()}
      </Popover>
    </>
  );
};

export default ThaiDatePicker;
